// LocationDropPage.tsx
import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {Form, Formik} from 'formik'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import DataTable, {TableColumn} from '../../../../shared/components/DataTable'
import SelectInput from '../../../../shared/components/SelectInput'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {loadBranches} from '../branch/Api'
import {IBranchModel} from '../branch/BranchModel'
import {loadLocationDropList, loadLocations} from './Api'
import {
  ILocationDropFilter,
  ILocationDropModel,
  ILocationModel,
  initialLocationDropFilter,
} from './LocationModel'

const PAGE_SIZE = 20

function buildColumns(): TableColumn<ILocationDropModel>[] {
  const formatNumber = (value: number | null | undefined): string =>
    value == null ? '0' : String(value)

  return [
    {
      key: 'branchName',
      label: 'Şube Adı',
      sortable: true,
      className: 'fw-semibold text-gray-800 min-w-150px',
    },
    {
      key: 'pickupLocationName',
      label: 'Çıkış Lokasyon',
      sortable: true,
      className: 'text-gray-800 min-w-150px',
    },
    {
      key: 'returnLocationName',
      label: 'Dönüş Lokasyon',
      sortable: true,
      className: 'text-gray-800 min-w-150px',
    },
    {
      key: 'dropFee',
      label: 'Drop-1',
      align: 'right',
      sortable: true,
      className: 'text-gray-800',
      render: (value: number) => (
        <span className={value === -1 ? 'text-muted' : ''}>{formatNumber(value)}</span>
      ),
    },
    {
      key: 'supplyDuration',
      label: 'Man Süresi',
      align: 'right',
      sortable: true,
      className: 'text-gray-800',
      render: (value: number) => formatNumber(value),
    },
    {
      key: 'minRental',
      label: 'Min. Gün',
      align: 'right',
      sortable: true,
      className: 'text-gray-800',
      render: (value: number) => formatNumber(value),
    },
  ]
}

export const LocationDropPage: FC = () => {
  const [rows, setRows] = useState<ILocationDropModel[]>([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [totalElements, setTotalElements] = useState(0)
  const [pageSize] = useState(PAGE_SIZE)
  const [sortBy, setSortBy] = useState<string>('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const [branches, setBranches] = useState<IBranchModel[]>([])
  const [locations, setLocations] = useState<ILocationModel[]>([])

  const [filterValues, setFilterValues] =
    useState<ILocationDropFilter>(initialLocationDropFilter)
  /** İlk açılışta liste boş; en az bir kez Filtrele sonrası sayfalama ve veri yüklenir */
  const [hasFiltered, setHasFiltered] = useState(false)
  const [filterOpen, setFilterOpen] = useState(true)

  const columns = useMemo(() => buildColumns(), [])

  // Şube ve lokasyonları dropdown'lar için yükle
  useEffect(() => {
    let cancelled = false
    Promise.all([
      loadBranches(0, 500, 'branchName', 'asc')
        .then((res) => res.data?.payload?.content || (res.data as any)?.content || [])
        .catch(() => [] as IBranchModel[]),
      loadLocations(0, 500, 'name', 'asc')
        .then((res) => res.data?.payload?.content || (res.data as any)?.content || [])
        .catch(() => [] as ILocationModel[]),
    ]).then(([branchList, locationList]) => {
      if (cancelled) return
      setBranches(branchList as IBranchModel[])
      setLocations(locationList as ILocationModel[])
    })
    return () => {
      cancelled = true
    }
  }, [])

  const branchOptions = useMemo(
    () =>
      branches.map((b) => ({
        value: String(b.id ?? ''),
        label: b.branchName || '',
      })),
    [branches]
  )

  const locationOptions = useMemo(
    () =>
      locations.map((l) => ({
        value: String(l.id ?? ''),
        label: l.name || '',
      })),
    [locations]
  )

  const runFilter = useCallback(
    async (
      values: ILocationDropFilter,
      page: number,
      sort?: string,
      order?: 'asc' | 'desc'
    ) => {
      setLoading(true)
      try {
        const res = await loadLocationDropList(page, pageSize, values, sort, order)
        const payload = res.data?.payload || (res.data as any)
        const content: ILocationDropModel[] = payload?.content ?? []
        // API id alanını null döndüğü için tabloya benzersiz key sağlamak amacıyla
        // satıra synthetic id ekleniyor
        const withIds = content.map((item, idx) => ({
          ...item,
          id:
            item.id ||
            `${item.branchName}__${item.pickupLocationName}__${item.returnLocationName}__${idx}`,
        }))
        setRows(withIds)
        setTotalPages(payload?.totalPages ?? 1)
        setTotalElements(payload?.totalElements ?? content.length)
      } catch (err: unknown) {
        const e = err as {response?: {status?: number}}
        if (e?.response?.status === 401) {
          window.location.href = '/login'
          return
        }
        console.error(err)
        showErrorToast('Lokasyon drop listesi yüklenirken bir hata oluştu')
      } finally {
        setLoading(false)
      }
    },
    [pageSize]
  )

  const handleSubmit = async (values: ILocationDropFilter) => {
    setFilterValues(values)
    setCurrentPage(0)
    setHasFiltered(true)
    await runFilter(values, 0, sortBy, sortOrder)
  }

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (!hasFiltered) return
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        runFilter(filterValues, newPage, sortBy, sortOrder)
      }
    },
    [totalPages, filterValues, runFilter, hasFiltered, sortBy, sortOrder]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      if (!hasFiltered) return
      setSortBy(column)
      setSortOrder(order)
      runFilter(filterValues, currentPage, column, order)
    },
    [currentPage, filterValues, runFilter, hasFiltered]
  )

  const handleExportExcel = () => {
    showSuccessToast("Excel'e aktarıldı")
  }

  return (
    <>
      <div className='card'>
        <div className='card-header border-0 pt-6'>
          <div className='card-title'>
            <div className='d-flex align-items-center gap-2 my-1'>
              <i className='bi bi-building fs-1 text-primary' aria-hidden />
              <h3 className='fw-bold m-0'>Lokasyon Drop Durumları</h3>
            </div>
          </div>
          <div className='card-toolbar d-flex gap-2'>
            <button
              type='button'
              className='btn btn-sm btn-success d-flex align-items-center'
              onClick={handleExportExcel}
              style={{backgroundColor: '#217346', borderColor: '#217346'}}
              title="Excel'e Aktar"
            >
              <i className='bi bi-file-earmark-spreadsheet me-1' aria-hidden />
              Excel'e Aktar
            </button>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFilterOpen((p) => !p)}
              aria-expanded={filterOpen}
            >
              <i className={`ki-duotone ki-${filterOpen ? 'up' : 'down'} fs-2`}>
                <span className='path1' />
                <span className='path2' />
              </i>
              {filterOpen ? 'Filtreleri gizle' : 'Filtreleri göster'}
            </button>
          </div>
        </div>

        <div className={`collapse ${filterOpen ? 'show' : ''}`}>
          <div className='card-body border-top pt-6'>
            <Formik
              initialValues={initialLocationDropFilter}
              enableReinitialize
              onSubmit={handleSubmit}
            >
              {() => (
                <Form className='form' id='locationDropFilterForm'>
                  <div className='row g-5 mb-5'>
                    <SelectInput
                      name='branchId'
                      label='İşlem Şube'
                      wrapperClassName='col-12 col-md-6 col-xl-4'
                      options={branchOptions}
                      firstItemLabel='Hepsi'
                    />
                    <SelectInput
                      name='pickupLocationId'
                      label='Çıkış Lokasyon'
                      wrapperClassName='col-12 col-md-6 col-xl-4'
                      options={locationOptions}
                      firstItemLabel='Hepsi'
                    />
                    <SelectInput
                      name='returnLocationId'
                      label='Dönüş Lokasyon'
                      wrapperClassName='col-12 col-md-6 col-xl-4'
                      options={locationOptions}
                      firstItemLabel='Hepsi'
                    />
                  </div>

                  <div className='d-flex flex-wrap align-items-center gap-4'>
                    <button type='submit' className='btn btn-primary' disabled={loading}>
                      {loading ? (
                        <span className='indicator-progress' style={{display: 'block'}}>
                          Lütfen bekleyin...
                          <span className='spinner-border spinner-border-sm align-middle ms-2' />
                        </span>
                      ) : (
                        <>
                          <i className='bi bi-funnel me-2' aria-hidden />
                          Filtrele
                        </>
                      )}
                    </button>
                    <div className='border border-gray-300 rounded px-4 py-2 bg-light'>
                      <span className='text-gray-700 fw-semibold fs-7'>
                        Kayıt Sayısı: {totalElements}
                      </span>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>

        <div className='card-body pt-0'>
          <DataTable<ILocationDropModel>
            data={rows}
            columns={columns}
            actions={[]}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            loading={loading}
            emptyMessage={
              hasFiltered
                ? 'Kayıt bulunamadı'
                : 'Listeyi yüklemek için Filtrele butonuna basın.'
            }
            tableClassName='table-row-dashed fs-8 text-nowrap'
            showPagination={hasFiltered}
          />
        </div>
      </div>

      <ToastContainer position='top-right' autoClose={5000} newestOnTop pauseOnHover />
    </>
  )
}
